import { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  BadgePercent,
  BarChart3,
  BarChartBig,
  Bell,
  CalendarCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Crown,
  IndianRupee,
  LandmarkIcon,
  LayoutDashboard,
  ListChecks,
  LucideShoppingCart,
  Menu,
  Power,
  Settings,
  UserCircle,
  Users,
} from "lucide-react";

import { useGlobalContext } from "../GlobalContext";

import Logo from "../Images/Packky.svg";

import Logo1 from "../Images/P.svg";

import ConfirmLogoutModal from "./Utils/ConfirmLogoutModal";

// Only explicit sidebar controls can change its persisted open/close state.
const SIDEBAR_OPEN_KEY = "packky_admin_sidebar_open";

/* =========================================================

   ICON MAP

========================================================= */

const iconMap = {
  Dashboard: LayoutDashboard,

  Operations: ClipboardList,

  Business: BadgePercent,

  Finance: IndianRupee,

  Reports: BarChartBig,

  Administration: Settings,

  // Submenus

  Tasks: ClipboardList,

  Task: ClipboardList,

  Driver: UserCircle,

  Drivers: UserCircle,

  Teams: Users,

  Team: Users,

  Slots: ListChecks,

  Slot: ListChecks,

  Attendance: CalendarCheck,

  Settings: Settings,

  Setting: Settings,

  Banks: LandmarkIcon,

  Bank: LandmarkIcon,

  Settlement: BarChart3,

  Notification: Bell,

  Notifications: Bell,

  Shops: LucideShoppingCart,

  Shop: LucideShoppingCart,

  Store: LandmarkIcon,

  Admin: Users,

  Tier: Crown,

  Tiers: Crown,
};

const getIcon = (title) => {
  return iconMap?.[title] || LayoutDashboard;
};

const normalizeRoute = (route = "") => {
  const value = String(route || "").trim();

  if (!value) return "";

  return value.startsWith("/") ? value : `/${value}`;
};

const getParentTargetRoute = (item) => {
  if (!item) return "";

  const submenus = Array.isArray(item?.submenus) ? item.submenus : [];

  // If submenu exists, parent click should open the first submenu

  const firstValidSubmenu = submenus.find((submenu) =>
    normalizeRoute(submenu?.submenu_url),
  );

  return (
    normalizeRoute(firstValidSubmenu?.submenu_url) ||
    normalizeRoute(item?.menu_url)
  );
};

/* =========================================================

   SIDEBAR

========================================================= */

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const { baseurl, token, setIsUnauthorised, AdminId, logoutAdmin } =
    useGlobalContext();

  const navigate = useNavigate();

  const location = useLocation();

  const [menuItems, setMenuItems] = useState([]);

  const [expandedMenus, setExpandedMenus] = useState({});

  const [volunteerInfo, setVolunteerInfo] = useState(null);

  // Preserve open/closed state even if a parent layout remounts on route change.
  // Navigation itself must never collapse the sidebar.
  useEffect(() => {
    const saved = sessionStorage.getItem(SIDEBAR_OPEN_KEY);
    if (saved === null) {
      sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen));
      return;
    }

    const shouldBeOpen = saved === "true";
    if (sidebarOpen !== shouldBeOpen) {
      setSidebarOpen(shouldBeOpen);
    }
  }, [sidebarOpen, setSidebarOpen]);

  // Use ONLY for direct user actions (toggle button or overlay click).
  const changeSidebarOpenManually = (nextOpen) => {
    sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(nextOpen));
    setSidebarOpen(nextOpen);
  };

  const navigateKeepingSidebar = (route) => {
    sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen));
    navigate(route);
  };

  const isOnline = true;

  /* =======================================================

     LOGOUT

  ======================================================= */

  const handleAdminLogout = () => {
    logoutAdmin();

    navigate("/login", {
      replace: true,
    });
  };

  /* =======================================================

     ADMIN INFO

  ======================================================= */

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin");

      if (!raw) return;

      const parsed = JSON.parse(raw);

      setVolunteerInfo(parsed);
    } catch (error) {
      console.error("Failed to parse admin data from session", error);
    }
  }, []);

  /* =======================================================

     GET SIDEBAR ACCESS

  ======================================================= */

  useEffect(() => {
    const fetchAccessMenu = async () => {
      try {
        const res = await fetch(`${baseurl}/api/Admin/v1.0/getUserAccess`, {
          method: "POST",

          headers: {
            Authorization: `Basic ${token}`,

            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            userid: AdminId,
          }),
        });

        const data = await res.json();

        console.log("GET USER ACCESS RESPONSE =>", data);

        if (data?.responseCode === "00") {
          const apiMenus = Array.isArray(data?.response) ? data.response : [];

          const mappedMenus = apiMenus

            .filter(
              (menu) =>
                menu &&
                menu?.status === "Y" &&
                menu?.menu_title &&
                menu?.menu_url,
            )

            .map((menu) => {
              const submenus = Array.isArray(menu?.submenus)
                ? menu.submenus

                    .filter(
                      (submenu) =>
                        submenu &&
                        submenu?.submenu_title &&
                        submenu?.submenu_url,
                    )

                    .map((submenu) => ({
                      id: submenu?.id ?? `${menu?.id}-${submenu?.submenu_url}`,

                      submenu_title: submenu?.submenu_title || "",

                      submenu_icon: submenu?.submenu_icon || "",

                      submenu_url: submenu?.submenu_url || "",
                    }))
                : [];

              return {
                id:
                  menu?.id ??
                  menu?.pk_menu_id ??
                  `${menu?.menu_title}-${menu?.menu_url}`,

                pk_menu_id: menu?.pk_menu_id,

                pk_submenu_id: menu?.pk_submenu_id,

                menu_title: menu?.menu_title || "",

                menu_icon: menu?.menu_icon || "",

                menu_url: menu?.menu_url || "",

                menu_type: menu?.menu_type || "MAIN",

                status: menu?.status || "N",

                submenus,
              };
            });

          console.log("FINAL SIDEBAR MENU =>", mappedMenus);

          setMenuItems(mappedMenus);

          return;
        }

        if (data?.responseCode === "401" || res?.status === 401) {
          setIsUnauthorised(true);
        }
      } catch (error) {
        console.error("Error fetching sidebar access", error);
      }
    };

    if (baseurl && token && AdminId) {
      fetchAccessMenu();
    }
  }, [baseurl, token, AdminId, setIsUnauthorised]);

  /* =======================================================

     ROUTE ACTIVE

  ======================================================= */

  const isRouteActive = (route) => {
    const normalizedRoute = normalizeRoute(route);

    const currentPath = normalizeRoute(location.pathname);

    if (!normalizedRoute || !currentPath) return false;

    // Exact match

    if (currentPath === normalizedRoute) {
      return true;
    }

    // Support nested routes:

    // /operation/tasks/123 -> /operation/tasks

    // /business/store/edit/1 -> /business/store

    if (currentPath.startsWith(`${normalizedRoute}/`)) {
      return true;
    }

    return false;
  };

  /* =======================================================

     CHECK IF SUBMENU SHOULD BE SHOWN

  ======================================================= */

  const getDisplaySubmenus = (item) => {
    return Array.isArray(item?.submenus) ? item.submenus : [];
  };

  /* =======================================================

     PARENT ACTIVE

  ======================================================= */

  const isParentActive = (item) => {
    if (!item) return false;

    const targetRoute = getParentTargetRoute(item);

    // Direct match (parent's own / first-submenu target route)

    if (isRouteActive(targetRoute)) {
      return true;
    }

    // Also active if ANY of its submenus is the active route

    const children = getDisplaySubmenus(item);

    return children.some((submenu) => isRouteActive(submenu?.submenu_url));
  };

  /* =======================================================

     EXPAND / COLLAPSE

  ======================================================= */

  const toggleMenu = (menuId) => {
    setExpandedMenus((previous) => ({
      ...previous,

      [menuId]: !previous?.[menuId],
    }));
  };

  const handleArrowClick = (item) => {
    if (!item) return;
    setExpandedMenus((previous) => ({
      ...previous,
      [item.id]: !previous?.[item.id],
    }));
  };

  /* =======================================================

     PARENT NAVIGATION

  ======================================================= */

  const handleParentNavigate = (item, closeMobile = false) => {
    if (!item) return;

    const hasChildren =
      Array.isArray(item?.submenus) && item.submenus.length > 0;

    if (hasChildren && (closeMobile || sidebarOpen)) {
      setExpandedMenus((previous) => ({
        ...previous,

        [item.id]: true,
      }));

      return;
    }

    const route = hasChildren
      ? normalizeRoute(item?.menu_url) || getParentTargetRoute(item)
      : normalizeRoute(item?.menu_url);

    if (!route) {
      console.warn("No route found for menu:", item);

      return;
    }

    navigateKeepingSidebar(route);

    // Sidebar stays open after navigation
  };

  /* =======================================================

     SUBMENU NAVIGATION

  ======================================================= */

  const handleSubmenuNavigate = (submenu, parentItem, closeMobile = false) => {
    const route = normalizeRoute(submenu?.submenu_url);

    if (!route) {
      console.warn("Submenu route missing:", submenu);

      return;
    }

    // Keep selected parent menu expanded

    if (parentItem?.id != null) {
      setExpandedMenus((previous) => ({
        ...previous,

        [parentItem.id]: true,
      }));
    }

    navigateKeepingSidebar(route);

    // Do not automatically close sidebar
  };

  /* =======================================================

     AUTO EXPAND CURRENT NESTED ROUTE

  ======================================================= */

  useEffect(() => {
    if (!menuItems.length) return;

    let activeMenuId = null;

    for (const item of menuItems) {
      const children = getDisplaySubmenus(item);

      const activeChild = children.some((submenu) =>
        isRouteActive(submenu?.submenu_url),
      );

      if (activeChild) {
        activeMenuId = item.id;

        break;
      }
    }

    if (activeMenuId === null) return;

    setExpandedMenus((previous) => ({
      ...previous,

      [activeMenuId]: true,
    }));
  }, [location.pathname, menuItems]);

  /* =======================================================

     USER CARD

  ======================================================= */

  const VolunteerStatusCard = ({ collapsed = false }) => {
    if (!volunteerInfo) {
      return null;
    }

    const name = volunteerInfo?.userName || "Volunteer";

    const role = volunteerInfo?.role || volunteerInfo?.userType || "User";

    if (collapsed) {
      return (
        <div className="px-2 pb-3">
          <div className="flex min-h-[60px] flex-col items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 p-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-bold text-[#FC981B] shadow-sm">
              {name?.charAt(0)?.toUpperCase() || "V"}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="px-3 pb-3">
        <div className="rounded-2xl border border-white/20 bg-white/10 p-3 shadow-sm backdrop-blur-md">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-[#FC981B] shadow-sm">
              {name?.charAt(0)?.toUpperCase() || "V"}
            </div>

            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">
                {name}
              </div>

              <div className="mt-0.5 truncate text-[11px] text-white/75">
                {role}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  isOnline ? "bg-emerald-400" : "bg-slate-300"
                }`}
              />

              <span className="text-xs font-medium text-white">
                {isOnline ? "Online" : "Offline"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* =======================================================

     RENDER

  ======================================================= */

  return (
    <>
      {/* ===================================================

          MOBILE / TABLET TOGGLE

      =================================================== */}

      {/* ===================================================

          MOBILE / TABLET SIDEBAR (slide-over)

      =================================================== */}

      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/20 lg:hidden"
            onClick={() => changeSidebarOpenManually(false)}
          />

          <div className="fixed bottom-0 left-0 top-0 z-40 flex w-[82vw] max-w-[300px] flex-col overflow-hidden bg-[#FC981B] text-white shadow-xl lg:hidden">
            {/* Header */}

            <div className="flex h-[75px] gap-4 shrink-0 items-center border-b border-white/20 px-4">
              <div className="flex h-11 w-full items-center rounded-3xl bg-white px-4">
                <img className="h-6 w-auto" src={Logo} alt="Packky" />
              </div>

              <button
                type="button"
                onClick={() => changeSidebarOpenManually(!sidebarOpen)}
                className="relative z-50 rounded-lg text-[#FC981B] p-2 bg-white shadow-lg lg:hidden"
                aria-label="Toggle navigation"
              >
                <Menu size={18} />
              </button>
            </div>

            {/* Navigation */}

            <nav className="min-h-0 flex-1 overflow-y-auto py-3">
              <div className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = getIcon(item?.menu_title);

                  const children = getDisplaySubmenus(item);

                  const hasChildren = children.length > 0;

                  const expanded = Boolean(expandedMenus?.[item.id]);

                  const parentActive = isParentActive(item);

                  return (
                    <div key={`${item.id}-${item.menu_url}`}>
                      {/* Parent row */}

                      <div
                        className={`relative flex h-11 w-full items-center transition-colors ${
                          parentActive
                            ? "bg-white/30 shadow-inner"
                            : "hover:bg-white/15"
                        }`}
                      >
                        {parentActive && (
                          <span className="absolute bottom-1 left-0 top-1 w-1 rounded-r-full bg-white" />
                        )}

                        {/* Parent click: opens submenu list (or navigates if no children) */}

                        <button
                          type="button"
                          onClick={() => handleParentNavigate(item, true)}
                          className="flex h-11 min-w-0 flex-1 items-center gap-3 px-4"
                        >
                          <Icon size={20} className="shrink-0" />

                          <span className="truncate text-sm font-semibold">
                            {item?.menu_title}
                          </span>
                        </button>

                        {/* Arrow */}

                        {hasChildren && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();

                              handleArrowClick(item);
                            }}
                            className="mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg hover:bg-white/15"
                          >
                            {expanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Submenus */}

                      {hasChildren && expanded && (
                        <div className="bg-black/5 py-1">
                          {children.map((submenu) => {
                            const SubIcon = getIcon(submenu?.submenu_title);

                            const active = isRouteActive(submenu?.submenu_url);

                            return (
                              <button
                                type="button"
                                key={`${item.id}-${submenu.id}-${submenu.submenu_url}`}
                                onClick={() =>
                                  handleSubmenuNavigate(submenu, item, true)
                                }
                                className={`relative flex min-h-10 w-full items-center gap-3 py-2 pl-9 pr-3 text-left transition ${
                                  active
                                    ? "bg-white/30 text-white font-semibold"
                                    : "text-white/80 hover:bg-white/10 hover:text-white"
                                }`}
                              >
                                {active && (
                                  <span className="absolute bottom-1 left-0 top-1 w-1 rounded-r-full bg-white" />
                                )}

                                <SubIcon size={16} className="shrink-0" />

                                <span className="truncate text-xs font-medium">
                                  {submenu?.submenu_title}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </nav>

            {/* Logout */}

            <div className="shrink-0 border-t border-white/20 p-3">
              <ConfirmLogoutModal
                onConfirm={handleAdminLogout}
                trigger={
                  <div className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 transition hover:bg-red-500">
                    <Power size={18} />

                    <span className="text-sm font-medium">Logout</span>
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}

      {/* ===================================================

          DESKTOP SIDEBAR

      =================================================== */}

      <aside
        className={`hidden h-full min-h-0 flex-col bg-[#FC981B] text-white shadow-lg transition-[width] duration-200 ease-in-out lg:flex ${
          sidebarOpen ? "w-56 xl:w-64" : "w-16"
        }`}
      >
        {/* Header */}

        <div
          className={`flex h-[88px] min-h-[88px] shrink-0 items-center border-b border-white/20 ${
            sidebarOpen ? "justify-between px-4" : "justify-center px-2"
          }`}
        >
          {sidebarOpen && (
            <div className="flex h-[52px] min-w-0 flex-1 items-center rounded-[2rem] bg-white px-4">
              <img
                className="h-7 max-w-full object-contain"
                src={Logo}
                alt="Packky"
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => changeSidebarOpenManually(!sidebarOpen)}
            className={`flex shrink-0 items-center justify-center transition-colors ${
              sidebarOpen
                ? "ml-3 h-9 w-9 rounded-full hover:bg-white/20"
                : "h-11 w-11 rounded-xl bg-white p-1.5 shadow-sm"
            }`}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? (
              <ChevronLeft size={20} />
            ) : (
              <img
                className="h-full w-full rounded-lg object-contain"
                src={Logo1}
                alt="Open sidebar"
              />
            )}
          </button>
        </div>

        {/* Navigation */}

        <nav className="min-h-0 flex-1 overflow-y-auto py-3">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = getIcon(item?.menu_title);

              const children = getDisplaySubmenus(item);

              const hasChildren = children.length > 0;

              const expanded = Boolean(expandedMenus?.[item.id]);

              const parentActive = isParentActive(item);

              return (
                <div key={`${item.id}-${item.menu_url}`}>
                  {/* Parent */}

                  <div
                    className={`relative flex h-11 w-full items-center transition-colors ${
                      parentActive ? "bg-white/20" : "hover:bg-white/15"
                    }`}
                  >
                    {parentActive && (
                      <span className="absolute bottom-1 left-0 top-1 w-1 rounded-r-full bg-white" />
                    )}

                    {/* Parent button: opens submenu list when expanded, navigates otherwise */}

                    <button
                      type="button"
                      title={!sidebarOpen ? item?.menu_title : ""}
                      onClick={() => {
                        handleParentNavigate(item);
                      }}
                      className={`flex h-11 min-w-0 flex-1 items-center ${
                        sidebarOpen
                          ? "justify-start gap-3 px-4"
                          : "justify-center px-0"
                      }`}
                    >
                      <Icon size={20} className="shrink-0" />

                      {sidebarOpen && (
                        <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold">
                          {item?.menu_title}
                        </span>
                      )}
                    </button>

                    {/* Separate expand arrow */}

                    {sidebarOpen && hasChildren && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();

                          handleArrowClick(item);
                        }}
                        className="mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition hover:bg-white/15"
                        aria-label={`Open ${item?.menu_title} submenu`}
                      >
                        {expanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Submenu */}

                  {sidebarOpen && hasChildren && expanded && (
                    <div className="bg-black/5 py-1">
                      {children.map((submenu) => {
                        const SubIcon = getIcon(submenu?.submenu_title);

                        const active = isRouteActive(submenu?.submenu_url);

                        return (
                          <button
                            type="button"
                            key={`${item.id}-${submenu.id}-${submenu.submenu_url}`}
                            onClick={() => handleSubmenuNavigate(submenu, item)}
                            className="relative flex min-h-7 w-full items-center py-0.5 pl-9 pr-3 text-left transition"
                          >
                            {active && (
                              <span className="absolute bottom-1 left-7 top-1 w-1 rounded-r-full bg-white" />
                            )}

                            <span
                              className={`inline-flex min-w-0 items-center gap-3 rounded-lg px-3 py-2 transition ${
                                active
                                  ? "bg-white/20 text-white"
                                  : "text-white/80 hover:bg-white/10 hover:text-white"
                              }`}
                            >
                              <SubIcon size={16} className="shrink-0" />

                              <span className="truncate text-xs font-medium">
                                {submenu?.submenu_title}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer */}

        <div className="shrink-0 border-t border-white/20 pt-3">
          <VolunteerStatusCard collapsed={!sidebarOpen} />

          <div className={sidebarOpen ? "px-3 pb-4" : "px-2 pb-4"}>
            <ConfirmLogoutModal
              onConfirm={handleAdminLogout}
              trigger={
                <div
                  title={!sidebarOpen ? "Logout" : ""}
                  className={`flex h-10 cursor-pointer items-center rounded-xl border border-white/15 bg-white/10 text-white transition-all hover:bg-red-500 ${
                    sidebarOpen
                      ? "justify-center gap-2 px-3"
                      : "justify-center px-0"
                  }`}
                >
                  <Power size={18} className="shrink-0" />

                  {sidebarOpen && (
                    <span className="text-sm font-medium">Logout</span>
                  )}
                </div>
              }
            />
          </div>
        </div>
      </aside>
    </>
  );
}
